
import { supabase } from "@/integrations/supabase/client";
import { ReagentRequest } from "@/types/reagentRequest.types";
import { toast } from "sonner";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger("CreateRequestService");

// Create a new reagent request
export const createReagentRequest = async (requestData: Partial<ReagentRequest>): Promise<ReagentRequest | null> => {
  try {
    // Ensure name is provided since it's required in the database
    if (!requestData.name) {
      throw new Error("Reagent name is required");
    }

    // Log the request data to help with debugging
    logger.debug("Creating reagent request with data:", requestData);

    const { data, error } = await supabase
      .from("reagent_requests")
      .insert({
        user_id: requestData.user_id,
        name: requestData.name,
        catalogue_number: requestData.catalogue_number,
        category_id: requestData.category_id,
        description: requestData.description,
        amount: requestData.amount,
        amount_unit: requestData.amount_unit,
        number_of_packages: requestData.number_of_packages,
        storage_conditions: requestData.storage_conditions,
        city: requestData.city,
        country: requestData.country
      })
      .select("*")
      .single();

    if (error) {
      logger.error("Error creating reagent request:", error);
      toast.error("Failed to create reagent request: " + error.message);
      throw error;
    }

    toast.success("Reagent request created successfully!");
    
    // Send push notifications to other users about the new reagent request
    try {
      logger.info('Starting reagent request notification process for request:', data.id);
      const { sendReagentRequestNotifications } = await import('@/services/notifications/reagentRequestNotification');
      
      // Get requester info for notification
      const { data: requesterProfile } = await supabase
        .from('profiles')
        .select('display_username, username')
        .eq('id', requestData.user_id)
        .single();
      
      const { data: categoryData } = await supabase
        .from('categories')
        .select('name')
        .eq('id', requestData.category_id)
        .single();
      
      const requesterName = requesterProfile?.display_username || requesterProfile?.username || 'Someone';
      logger.info('Sending reagent request notifications with data:', {
        requestId: data.id,
        reagentName: data.name,
        requesterName,
        requesterId: requestData.user_id,
        category: categoryData?.name,
        city: requestData.city,
        country: requestData.country
      });
      
      // Send notifications and handle the result properly
      const notificationResult = await sendReagentRequestNotifications({
        requestId: data.id,
        reagentName: data.name,
        requesterName,
        requesterId: requestData.user_id!,
        category: categoryData?.name,
        city: requestData.city || undefined,
        country: requestData.country || undefined
      });
      
      if (notificationResult) {
        logger.info('Reagent request notifications sent successfully');
      } else {
        logger.error('Reagent request notifications failed - check logs for details');
      }
    } catch (notificationError) {
      logger.error('Error in reagent request notification process:', notificationError);
    }
    
    return data as ReagentRequest;
  } catch (error) {
    logger.error("Exception in createReagentRequest:", error);
    
    // More specific error message when it's related to a missing column
    if (error instanceof Error && error.message.includes("column")) {
      toast.error("Database error: " + error.message);
    } else {
      toast.error("Failed to create reagent request");
    }
    
    return null;
  }
};
